import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaArrowRight, FaGlobe } from "react-icons/fa";
import Seo from "../components/Seo";
import { Helmet } from "react-helmet-async";
import { pathFor } from "../lib/i18nRoutes";
import { LANDING_CONTENT, LANDING_UI, LANDING_PAGES, NAV_LABELS } from "../lib/landingContent";

// Template das landing pages de SEO (Fase C). Recebe `pageKey` e `basePath`
// (caminho PT, sem prefixo). O conteúdo vem de lib/landingContent por idioma;
// o Seo cuida de canonical/hreflang; e emitimos o schema FAQPage do idioma
// atual (casando com a FAQ visível).
const LandingPage = ({ pageKey, basePath }) => {
  const { i18n } = useTranslation();
  const lang = ["pt", "es", "en"].includes(i18n.language) ? i18n.language : "pt";
  const c = LANDING_CONTENT[pageKey]?.[lang] || LANDING_CONTENT[pageKey]?.pt;
  const ui = LANDING_UI[lang] || LANDING_UI.pt;
  if (!c) return null;

  const ctaHref = pathFor(lang, "/register");

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="w-full overflow-x-hidden">
      <Seo title={c.title} description={c.description} path={basePath} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="w-full bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-14 sm:py-20 md:py-24">
        <div className="w-full px-4 md:px-6 mx-auto max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            {c.h1}
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-blue-700 font-medium">{c.subtitle}</p>
          <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {c.intro}
          </p>
          <div className="mt-8">
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base shadow-lg shadow-blue-500/25 transition-colors"
            >
              {c.ctaLabel} <FaArrowRight className="text-sm" />
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {ui.trust.map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                <FaCheckCircle className="text-green-500 text-sm" /> {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Seções de texto (páginas informacionais) */}
      {Array.isArray(c.sections) && c.sections.length > 0 && (
        <section className="w-full bg-white py-12 sm:py-16">
          <div className="w-full px-4 md:px-6 mx-auto max-w-3xl">
            {c.sections.map((s) => (
              <div key={s.h2} className="mb-8 last:mb-0">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">{s.h2}</h2>
                <p className="text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Benefícios */}
      {Array.isArray(c.benefits) && c.benefits.length > 0 && (
        <section className="w-full bg-white py-12 sm:py-16 md:py-20">
          <div className="w-full px-4 md:px-6 mx-auto max-w-5xl">
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-8 sm:mb-12">
              {ui.benefitsTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {c.benefits.map((b) => (
                <div key={b.title} className="rounded-2xl border border-gray-100 bg-gray-50 p-6 text-center sm:text-left">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600 mb-3">
                    <FaCheckCircle />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{b.title}</h3>
                  <p className="mt-1.5 text-gray-600 leading-relaxed">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="w-full bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="w-full px-4 md:px-6 mx-auto max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-8 sm:mb-12">
            {ui.faqTitle}
          </h2>
          <div className="space-y-3">
            {c.faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-gray-200 bg-white px-5 py-4 open:shadow-sm transition"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none text-gray-900 font-medium [&::-webkit-details-marker]:hidden">
                  <span>{f.q}</span>
                  <span className="ml-3 flex-shrink-0 text-gray-400 transition-transform group-open:rotate-180">▾</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Links relacionados (internos) — cruza com as outras landing pages e a Home. */}
      <section className="w-full bg-white py-10 sm:py-14 border-t border-gray-100">
        <div className="w-full px-4 md:px-6 mx-auto max-w-4xl">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{ui.relatedTitle}</h2>
          <div className="flex flex-wrap gap-2.5">
            {LANDING_PAGES.filter((p) => p.key !== pageKey).slice(0, 5).map((p) => (
              <Link
                key={p.key}
                to={pathFor(lang, p.path)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                {NAV_LABELS[p.key]?.[lang] || NAV_LABELS[p.key]?.pt}
              </Link>
            ))}
            <Link
              to={pathFor(lang, "/")}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              {ui.homeLabel}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="w-full bg-gradient-to-br from-blue-600 to-indigo-700 py-12 sm:py-16">
        <div className="w-full px-4 md:px-6 mx-auto max-w-3xl text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white mb-4">
            <FaGlobe />
          </div>
          <p className="text-lg sm:text-xl text-white/90 mb-6 max-w-xl mx-auto">{ui.ctaSub}</p>
          <Link
            to={ctaHref}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-blue-700 hover:bg-blue-50 font-semibold text-base shadow-lg transition-colors"
          >
            {c.ctaLabel} <FaArrowRight className="text-sm" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
